/**
 * Web-specific useAuthDialog hook
 * Manages authentication dialog state and handles login/register flows
 */

import { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppDispatch, setUser, setWallets, authService, type LoginRequest, type RegisterRequest, type ApiError, getUserProfile, saveAccessToken, saveRefreshToken, setShowOnboarding } from '@ideoz/core';
import { useGoogleAuth } from './useGoogleAuth';
import { GOOGLE_AUTH_CONFIG } from '../config/googleAuth';
import { getSafeRedirect } from '../utils/safeRedirect';

export interface UseAuthDialogReturn {
  isOpen: boolean;
  mode: 'login' | 'register';
  isLoading: boolean;
  error?: string;
  openRegister: (opts?: { fromChat?: boolean }) => void;
  openLogin: () => void;
  closeDialog: () => void;
  switchMode: (mode: 'login' | 'register') => void;
  handleLogin: (email: string, password: string, opts?: { pairingKey?: string | null; figmaSource?: string | null; redirectTo?: string | null }) => Promise<void>;
  handleRegister: (name: string, email: string, password: string, opts?: { fromChat?: boolean; pairingKey?: string | null; figmaSource?: string | null }) => Promise<void>;
  handleGoogleAuth: (opts?: { pairingKey?: string | null; figmaSource?: string | null; redirectTo?: string | null }) => Promise<void>;
  handleNavigateToForgotPassword: () => void;
  googleIsLoaded: boolean;
  onboardingSource?: 'homepage' | 'chat';
  setOnboardingSource?: (src: 'homepage' | 'chat' | undefined) => void;
}

export const useAuthDialog = (): UseAuthDialogReturn => {
  const [isOpen, setIsOpen] = useState(false);
  const [mode, setMode] = useState<'login' | 'register'>('login');
  const [isLoading, setIsLoading] = useState(false);
  const [onboardingSource, setOnboardingSource] = useState<'homepage' | 'chat' | undefined>(undefined);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  // Google Sign-In is a full-page redirect (see useGoogleAuth); the result
  // (token exchange + login completion) is handled by GoogleLoginCallback.tsx
  // after the browser navigates back from Google, not here.
  const {
    isLoaded: googleIsLoaded,
    isLoading: googleIsLoading,
    signInWithGoogle,
    error: googleError,
  } = useGoogleAuth({
    clientId: GOOGLE_AUTH_CONFIG.CLIENT_ID,
  });

  const openLogin = useCallback(() => {
    setMode('login');
    setIsOpen(true);
    setOnboardingSource(undefined);
  }, []);

  // Accepts an option to indicate if registration is from chat or homepage
  // Only homepage registration should trigger onboarding. All chat-based registrations (including create project from chat) should set onboardingSource to 'chat'.
  const openRegister = useCallback((opts?: { fromChat?: boolean }) => {
    setMode('register');
    setIsOpen(true);
    if (opts?.fromChat) {
      setOnboardingSource('chat');
    } else if (window.location.pathname === '/' || window.location.pathname === '/landing') {
      setOnboardingSource('homepage');
    } else {
      setOnboardingSource('chat');
    }
  }, []);

  const closeDialog = useCallback(() => {
    setIsOpen(false);
    setIsLoading(false);
  }, []);

  // Helper function to handle conversation page redirect logic
  const handleConversationRedirect = useCallback(() => {
    const pathname = window.location.pathname;
    const referrer = document.referrer;
    const isOnConversation = pathname === '/conversation' || pathname.startsWith('/conversation/');
    const cameFromConversation = referrer.includes('/conversation');
    
    if (isOnConversation) {
      closeDialog();
      return true; // Indicates we handled the redirect
    }
    if (cameFromConversation) {
      // Extract conversation path from referrer or navigate to base conversation
      const conversationMatch = referrer.match(/\/conversation[^?]*/);
      if (conversationMatch) {
        closeDialog();
        navigate(conversationMatch[0]);
        return true;
      }
      closeDialog();
      navigate('/conversation');
      return true;
    }
    return false; // Indicates we didn't handle redirect, caller should navigate to '/'
  }, [navigate, closeDialog]);

  const switchMode = useCallback((newMode: 'login' | 'register') => {
    setMode(newMode);
    setIsLoading(false);
  }, []);

  const handleLogin = useCallback(async (email: string, password: string, opts?: { pairingKey?: string | null; figmaSource?: string | null; redirectTo?: string | null }) => {
    setIsLoading(true);
    try {
      const loginData: LoginRequest = { email, password };
      loginData.platform = opts?.pairingKey ? 'plugin' : 'web';

      // Add pairing_key if provided
      if (opts?.pairingKey) {
        loginData.pairing_key = opts.pairingKey;
      }

      const response = await authService.login(loginData);

      if (!response.access_token) {
        throw new Error(response.message || 'Invalid login response from server');
      }

      saveAccessToken(response.access_token);
      saveRefreshToken(response.refresh_token);
      (window as any).appAccessToken = response.access_token;
      const accessToken = response.access_token;

      // Update Redux state with user data and check onboarding status
      const userData = await dispatch(getUserProfile(accessToken)).unwrap();

      // Check if coming from Figma plugin
      // The pairing_key is the critical parameter for plugin authentication
      if (opts?.pairingKey) {
        closeDialog();
        const redirectUrl = `/redirect?pairing_key=${opts.pairingKey}&figma_source=${opts.figmaSource || 'desktop'}`;
        navigate(redirectUrl);
        return;
      }

      // Check if onboarding should be shown based on metadata.isOnBoardingShown flag
      const pathname = window.location.pathname;
      const isOnLandingPage = pathname === '/' || pathname === '/landing';
      // Check both camelCase and snake_case, and handle string values
      const isOnBoardingShownRaw = userData?.metadata?.isOnBoardingShown ??
        userData?.metadata?.is_onboarding_shown ??
        false;
      const isOnBoardingShown = typeof isOnBoardingShownRaw === 'string'
        ? isOnBoardingShownRaw.toLowerCase() === 'true'
        : Boolean(isOnBoardingShownRaw);
      // Only show onboarding if first-time user on landing page
      if (isOnBoardingShown === false && isOnLandingPage) {
        dispatch(setShowOnboarding(true));
        navigate('/onboarding');
      } else {
        // All other cases - navigate to home page
        // Update onboarding flag if needed
        if (isOnBoardingShown === false) {
          authService.updateOnboardingShown().catch((error) => {
            console.error('Failed to update onboarding shown flag:', error);
          });
        }
        // Return the user to the URL they were trying to reach before being
        // bounced to login (ProtectedRoute passes it as `?redirect=`).
        const redirectTo = getSafeRedirect(opts?.redirectTo);
        if (redirectTo) {
          closeDialog();
          navigate(redirectTo);
          return;
        }
        // Don't redirect if user is on conversation page or came from conversation
        if (handleConversationRedirect()) {
          return;
        }
        navigate('/');
      }

      closeDialog();
    } catch (error: any) {
      const apiError = error as ApiError;
      throw new Error(apiError.message || 'Login failed');
    } finally {
      setIsLoading(false);
    }
  }, [dispatch, closeDialog, navigate]);

  const handleRegister = useCallback(
    async (name: string, email: string, password: string, opts?: { fromChat?: boolean; pairingKey?: string | null; figmaSource?: string | null }) => {
      setIsLoading(true);
      try {
        const registerData: RegisterRequest = {
          email,
          password,
          full_name: name,
          platform: opts?.pairingKey ? 'plugin' : 'web',
        };

        // Add pairing_key if provided
        if (opts?.pairingKey) {
          registerData.pairing_key = opts.pairingKey;
        }

        const response = await authService.register(registerData);

        if (!response.access_token) {
          throw new Error(response.message || 'Invalid registration response from server');
        }

        saveAccessToken(response.access_token);
        saveRefreshToken(response.refresh_token);
        (window as any).appAccessToken = response.access_token;
        const accessToken = response.access_token;

        // Update Redux state with user data and check onboarding status
        const userData = await dispatch(getUserProfile(accessToken)).unwrap();

        // Check if coming from Figma plugin
        // The pairing_key is the critical parameter for plugin authentication
        if (opts?.pairingKey) {
          closeDialog();
          const redirectUrl = `/redirect?pairing_key=${opts.pairingKey}&figma_source=${opts.figmaSource || 'desktop'}`;
          navigate(redirectUrl);
          return;
        }

        // Check if onboarding should be shown based on metadata.isOnBoardingShown flag
        const pathname = window.location.pathname;
        const isOnLandingPage = pathname === '/' || pathname === '/landing';
        // Check both camelCase and snake_case, and handle string values
        const isOnBoardingShownRaw = userData?.metadata?.isOnBoardingShown ??
          userData?.metadata?.is_onboarding_shown ??
          false;
        const isOnBoardingShown = typeof isOnBoardingShownRaw === 'string'
          ? isOnBoardingShownRaw.toLowerCase() === 'true'
          : Boolean(isOnBoardingShownRaw);
        // Only show onboarding if:
        // 1. First-time user (isOnBoardingShown === false)
        // 2. On landing page
        // 3. Not from chat (onboardingSource !== 'chat')
        const shouldShowOnboarding = isOnBoardingShown === false &&
          isOnLandingPage &&
          (onboardingSource === 'homepage' || onboardingSource === undefined);

        if (shouldShowOnboarding) {
          dispatch(setShowOnboarding(true));
          navigate('/onboarding');
        } else {
          // All other cases - navigate to home page
          // Update onboarding flag if needed
          if (isOnBoardingShown === false) {
            authService.updateOnboardingShown().catch((error) => {
              console.error('Failed to update onboarding shown flag:', error);
            });
          }
          // Don't redirect if user is on conversation page or came from conversation
          if (handleConversationRedirect()) {
            return;
          }
          navigate('/');
        }

        closeDialog();
      } catch (error: any) {
        const apiError = error as ApiError;
        throw new Error(apiError.message || 'Registration failed');
      } finally {
        setIsLoading(false);
      }
    },
    [dispatch, closeDialog, navigate, onboardingSource]
  );

  const handleGoogleAuth = useCallback(async (opts?: { pairingKey?: string | null; figmaSource?: string | null; redirectTo?: string | null }) => {
    if (!googleIsLoaded) {
      throw new Error('Google authentication is not ready yet');
    }

    if (googleError) {
      throw new Error(googleError);
    }

    // Navigates the whole page to Google — nothing resolves after this call;
    // the result is picked up by GoogleLoginCallback.tsx on the way back.
    signInWithGoogle(opts);
  }, [googleIsLoaded, googleError, signInWithGoogle]);

  const handleNavigateToForgotPassword = useCallback(() => {
    closeDialog();
    navigate('/forgot-password', { state: { from: 'authDialog' } });
  }, [closeDialog, navigate]);

  return {
    isOpen,
    mode,
    isLoading: isLoading || googleIsLoading,
    error: googleError || undefined,
    openLogin,
    openRegister,
    closeDialog,
    switchMode,
    handleLogin,
    handleRegister,
    handleGoogleAuth,
    handleNavigateToForgotPassword,
    googleIsLoaded,
    onboardingSource,
    setOnboardingSource,
  };
};
